

import React, { useState, useEffect, useCallback } from 'react';

// --- CSS Styles (add this inside your component file or link an external stylesheet) ---
const styles = `
.button-primary {
  background-color: #0d6efd;
  color: white;
  border: 1px solid #0d6efd;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
}
.button-primary:hover {
  background-color: #0b5ed7;
}
.button-primary:disabled {
  background-color: #a0c7ff;
  cursor: not-allowed;
}

.button-secondary {
  background-color: #ffffff;
  color: #495057;
  border: 1px solid #ced4da;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
}
.button-secondary:hover {
  background-color: #f8f9fa;
}
.button-secondary:disabled {
  background-color: #e9ecef;
  color: #6c757d;
  cursor: not-allowed;
}

.action-buttons-group {
    display: flex;
    gap: 10px;
    align-items: center;
}

.permissions-header-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.preset-apply-group {
    display: flex;
    align-items: center;
    gap: 8px;
}

.preset-apply-group select {
    padding: 8px;
    border-radius: 6px;
    border: 1px solid #ced4da;
}

/* New Modal Styles */
.modal-content.preset-modal {
    max-width: 500px;
    border-radius: 8px;
}

.preset-modal h2 {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 24px;
    text-align: center;
}

.preset-modal .form-group {
    margin-bottom: 16px;
}

.preset-modal label {
    font-weight: 500;
    margin-bottom: 8px;
    display: block;
}

.preset-modal input[type="text"] {
    width: 100%;
    padding: 10px;
    border: 1px solid #ced4da;
    border-radius: 6px;
}

.modal-permissions-list {
    border: 1px solid #dee2e6;
    border-radius: 6px;
    max-height: 250px;
    overflow-y: auto;
    padding: 8px;
}

.modal-permission-item {
    padding: 12px;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    margin-bottom: 8px;
}

.modal-permission-item:last-child {
    margin-bottom: 0;
}

.modal-permission-item label {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0;
    font-weight: normal;
    width: 100%;
}

.modal-permission-item input[type="checkbox"] {
    width: 18px;
    height: 18px;
}

.preset-modal .modal-actions {
    justify-content: center;
    margin-top: 24px;
}

.preset-modal .button-cancel {
    background-color: #6c757d;
    color: white;
    border: none;
}
.preset-modal .button-cancel:hover {
    background-color: #5a6268;
}

.preset-modal .button-create {
    background-color: #0d6efd;
    color: white;
    border: none;
}
.preset-modal .button-create:hover {
    background-color: #0b5ed7;
}
`;

// --- Configuration ---
const LOCAL_STORAGE_USER_KEY = 'zTaskUserDetails';
const AUTH_TOKEN_KEY = 'zTaskAuthToken';
const API_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;

// --- AssignRolesContent Component ---
const AssignRolesContent = ({ allRoles, isSuperAdmin, isAdminUser, isReportingManager }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [fetchUsersError, setFetchUsersError] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [roleIdToAssign, setRoleIdToAssign] = useState('');
  const [isSubmittingAssign, setIsSubmittingAssign] = useState(false);
  const [assignSuccessMessage, setAssignSuccessMessage] = useState('');
  const [assignErrorMessage, setAssignErrorMessage] = useState('');

  const fetchAllUsers = useCallback(async (userIdToRefresh = null) => {
    setIsLoadingUsers(true);
    setFetchUsersError('');
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      setFetchUsersError("Authentication token not found.");
      setIsLoadingUsers(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/get-users`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: `API Error: ${response.status}`}));
        throw new Error(errData.message || `Failed to fetch users: ${response.status}`);
      }
      const data = await response.json();
      const rawUsers = data.userData || [];

      const processedUsers = rawUsers
        .filter(apiUser => apiUser.active === true)
        .map(apiUser => {
            const currentApiRole = apiUser.role;
            let assignedRoleForState = null;

            if (currentApiRole && currentApiRole.roleId != null) {
                assignedRoleForState = {
                    roleId: currentApiRole.roleId,
                    name: currentApiRole.name,
                };
            }

            return {
                userId: apiUser.id,
                username: apiUser.email,
                apiName: apiUser.name,
                fullName: apiUser.name || 'N/A',
                email: apiUser.email,
                assignedRoles: assignedRoleForState ? [assignedRoleForState] : [],
            };
        })
        .sort((a, b) => a.fullName.localeCompare(b.fullName));

      setUsers(processedUsers);

      if (userIdToRefresh) {
        const refreshedUser = processedUsers.find(u => u.userId === userIdToRefresh);
        if (refreshedUser) {
            setSelectedUser(refreshedUser);
        }
      } else if (selectedUser) {
        const refreshedSelectedUser = processedUsers.find(u => u.userId === selectedUser.userId);
        setSelectedUser(refreshedSelectedUser || null);
      }

    } catch (error) {
      setFetchUsersError(`Error fetching users: ${error.message}`);
      setUsers([]);
      setSelectedUser(null);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [selectedUser?.userId]);

  useEffect(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setAssignSuccessMessage('');
    setAssignErrorMessage('');
  };

  const openAssignRoleModal = () => {
    if (!selectedUser) return;
    setRoleIdToAssign('');
    setAssignSuccessMessage('');
    setAssignErrorMessage('');
    setIsAssignModalOpen(true);
  };

  const closeAssignRoleModal = () => {
    setIsAssignModalOpen(false);
  };

  const handleAssignRoleToUser = async (event) => {
    event.preventDefault();
    if (!selectedUser || !roleIdToAssign) {
      setAssignErrorMessage("Please select a user and a role to assign.");
      return;
    }
    setIsSubmittingAssign(true);
    setAssignSuccessMessage('');
    setAssignErrorMessage('');
    const token = localStorage.getItem(AUTH_TOKEN_KEY);

    try {
      const queryParams = new URLSearchParams({
        user_id: selectedUser.userId.toString(),
        role_id: roleIdToAssign.toString(),
      });
      const response = await fetch(`${API_BASE_URL}/api/assign-role?${queryParams.toString()}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });

      let responseData;
      const responseText = await response.text();
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        if (response.ok) {
          responseData = { message: "Role assigned successfully (server response was not JSON)." };
        } else {
          responseData = { message: `Failed to assign role (server response was not JSON: ${responseText.substring(0, 100)})` };
        }
      }

      if (!response.ok) {
        throw new Error(responseData?.message || `API Error: ${response.status} - ${response.statusText}`);
      }

      setAssignSuccessMessage(responseData?.message || `Role successfully assigned to ${selectedUser.apiName || selectedUser.username}!`);
      await fetchAllUsers(selectedUser.userId);
      closeAssignRoleModal();

    } catch (error) {
      setAssignErrorMessage(`Failed to assign role: ${error.message}`);
    } finally {
      setIsSubmittingAssign(false);
    }
  };

  const availableRolesForAssignment = selectedUser && Array.isArray(allRoles)
    ? allRoles
        .filter(globalRole => !selectedUser.assignedRoles.some(assignedRole => assignedRole.roleId === globalRole.roleId))
        .sort((a, b) => a.name.localeCompare(b.name))
    : (allRoles || []);

  const filteredUsers = users.filter(user =>
    user.fullName.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    user.username.toLowerCase().includes(userSearchQuery.toLowerCase())
  );

  if (isLoadingUsers && users.length === 0) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading users...</div>;
  }
  if (fetchUsersError && users.length === 0 && !isLoadingUsers) {
    return <div style={{ padding: '20px', color: 'red', textAlign: 'center' }}>{fetchUsersError} <button onClick={() => fetchAllUsers()}>Retry</button></div>;
  }

  const canAssignRole = isSuperAdmin || isAdminUser || isReportingManager;

  return (
    <div className="assign-roles-content" style={{ padding: '20px' }}>
      <div className="assign-roles-header">
        <h3>User Role Assignments</h3>
      </div>
      <div className="assign-roles-layout">
        <div className="users-panel">
          <h4>Users</h4>
          <div className="user-search-container" style={{ padding: '0 10px 10px', boxSizing: 'border-box' }}>
            <input
              type="text"
              className="user-search-input"
              placeholder="Search by name or email..."
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
            />
          </div>
          <div className="users-table-header">
            <span>Full Name</span>
            <span>Email (as Username)</span>
          </div>
          {filteredUsers.length > 0 ? (
            <ul className="users-list">
              {filteredUsers.map(user => (
                <li
                  key={user.userId}
                  className={`user-list-item ${selectedUser?.userId === user.userId ? 'selected' : ''}`}
                  onClick={() => handleUserSelect(user)}
                >
                  <span>{user.fullName}</span>
                  <span>{user.username}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p>{!isLoadingUsers && 'No users found.'}</p>
          )}
           {fetchUsersError &&
            <div className="form-message error" style={{marginTop: '10px'}}>{fetchUsersError} <button onClick={() => fetchAllUsers()}>Retry</button></div>
          }
        </div>
        <div className="assigned-roles-panel">
          <div className="assigned-roles-panel-header">
            <h4>Assigned Roles {selectedUser ? `for ${selectedUser.apiName || selectedUser.username}` : ''}</h4>
            {canAssignRole && selectedUser && (
                <button
                    className="assign-role-action-button"
                    onClick={openAssignRoleModal}
                    disabled={isSubmittingAssign || isLoadingUsers}
                >
                    Assign Role
                </button>
            )}
          </div>
          {selectedUser ? (
            <>
              {assignSuccessMessage && <div className="form-message success" style={{marginBottom: '10px'}}>{assignSuccessMessage}</div>}
              {assignErrorMessage && !assignSuccessMessage && <div className="form-message error" style={{marginBottom: '10px'}}>{assignErrorMessage}</div>}
              {selectedUser.assignedRoles.length > 0 ? (
                <>
                  <div className="assigned-roles-table-header-simplified">
                    <span>Role</span>
                  </div>
                  <ul className="assigned-roles-list">
                    {[...selectedUser.assignedRoles].sort((a,b) => a.name.localeCompare(b.name)).map(assignedRole => (
                      <li key={assignedRole.roleId} className="assigned-role-item-simplified">
                        <span>{assignedRole.name}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                !assignSuccessMessage && !assignErrorMessage && <p>No roles assigned to this user.</p>
              )}
            </>
          ) : (
            <p>Select a user to see their assigned roles.</p>
          )}
        </div>
      </div>
      {isAssignModalOpen && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Assign Role to {selectedUser.apiName || selectedUser.username}</h2>
            <form onSubmit={handleAssignRoleToUser}>
              <div className="form-group">
                <label htmlFor="roleToAssignSelect">Select Role</label>
                <select
                  id="roleToAssignSelect"
                  value={roleIdToAssign}
                  onChange={(e) => setRoleIdToAssign(e.target.value)}
                  required
                  disabled={isSubmittingAssign}
                >
                  <option value="" disabled>-- Select a Role --</option>
                  {availableRolesForAssignment.length > 0 ? availableRolesForAssignment.map(role => (
                    <option key={role.roleId} value={role.roleId}>
                      {role.name}
                    </option>
                  )) : <option disabled>No new roles to assign or all roles assigned</option>}
                </select>
              </div>
              {assignErrorMessage && <div className="form-message error" style={{marginTop: '10px'}}>{assignErrorMessage}</div>}
              <div className="modal-actions">
                <button type="button" onClick={closeAssignRoleModal} className="button-cancel" disabled={isSubmittingAssign}>Cancel</button>
                <button type="submit" className="button-create" disabled={isSubmittingAssign || availableRolesForAssignment.length === 0 || !roleIdToAssign}>
                  {isSubmittingAssign ? 'Assigning...' : 'Assign Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// --- PermissionsPage Component Definition ---
const PermissionsPage = () => {
  const [activeTab, setActiveTab] = useState('manage');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [isReportingManager, setIsReportingManager] = useState(false);
  const [isLoadingUserRole, setIsLoadingUserRole] = useState(true);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [isSubmittingPermissions, setIsSubmittingPermissions] = useState(false);
  const [isSubmittingCreateRole, setIsSubmittingCreateRole] = useState(false);
  const [isApplyingPreset, setIsApplyingPreset] = useState(false);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [currentRolePermissions, setCurrentRolePermissions] = useState({});
  const [presetsWithDetails, setPresetsWithDetails] = useState(new Map());
  const [allSystemPermissions, setAllSystemPermissions] = useState([]);
  const [isLoadingSystemPermissions, setIsLoadingSystemPermissions] = useState(true);
  const [fetchSystemPermissionsError, setFetchSystemPermissionsError] = useState('');
  const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [isCreateDefaultSetModalOpen, setIsCreateDefaultSetModalOpen] = useState(false);
  const [permissionsForNewSet, setPermissionsForNewSet] = useState({});
  const [isSubmittingNewSet, setIsSubmittingNewSet] = useState(false);
  const [modalSetErrorMessage, setModalSetErrorMessage] = useState('');
  const [modalSetSuccessMessage, setModalSetSuccessMessage] = useState('');
  const [modalPermissionSearch, setModalPermissionSearch] = useState('');
  const [permissionSearchQuery, setPermissionSearchQuery] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [modalSuccessMessage, setModalSuccessMessage] = useState('');
  const [modalErrorMessage, setModalErrorMessage] = useState('');

  const checkUserRole = useCallback(() => {
    setIsLoadingUserRole(true);
    try {
      const userData = JSON.parse(localStorage.getItem(LOCAL_STORAGE_USER_KEY));
      const userRoleName = userData?.role?.name?.toUpperCase();
      setIsSuperAdmin(userRoleName === 'SUPER_ADMIN');
      setIsAdminUser(userRoleName === 'ADMIN');
      setIsReportingManager(userRoleName === 'REPORTING MANAGER');
    } catch (error) { /* Silent error */ }
    setIsLoadingUserRole(false);
  }, []);

  useEffect(() => {
    if (!isLoadingUserRole) {
      setActiveTab((isReportingManager && !isSuperAdmin) ? 'assign' : 'manage');
    }
  }, [isLoadingUserRole, isReportingManager, isSuperAdmin]);

  const fetchRoles = useCallback(async () => {
    setIsLoadingRoles(true);
    setErrorMessage('');
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      setErrorMessage("Authentication token not found.");
      setIsLoadingRoles(false);
      return [];
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/get-roles`, {
        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `API Error: ${response.status}` }));
        throw new Error(errorData.message || 'Failed to fetch roles');
      }
      const data = await response.json();
      if (!Array.isArray(data.roles)) throw new Error("Received unexpected data format for roles.");

      const processedRoles = data.roles
        .map(role => ({ ...role, permissions: Array.isArray(role.permissions) ? role.permissions : [] }))
        .sort((a, b) => a.name.localeCompare(b.name));
      
      setRoles(processedRoles);

      const presetsMap = new Map();
      processedRoles.forEach(role => {
        if (role.defaultPermissions) {
          const { defaultPermissionId, defaultPermissions } = role.defaultPermissions;
          if (!presetsMap.has(defaultPermissionId)) {
            presetsMap.set(defaultPermissionId, {
              id: defaultPermissionId,
              name: `Preset #${defaultPermissionId}`,
              permissions: defaultPermissions || []
            });
          }
        }
      });
      setPresetsWithDetails(presetsMap);

      return processedRoles;
    } catch (error) {
      setErrorMessage(`Error fetching system roles: ${error.message}`);
      setRoles([]);
      return [];
    } finally {
      setIsLoadingRoles(false);
    }
  }, []);

  const fetchAllSystemPermissions = useCallback(async () => {
    setIsLoadingSystemPermissions(true);
    setFetchSystemPermissionsError('');
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      setFetchSystemPermissionsError("Authentication token not found.");
      setIsLoadingSystemPermissions(false);
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/get-permissions`, {
        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `API Error: ${response.status}` }));
        throw new Error(errorData.message || 'Failed to fetch permissions');
      }
      const data = await response.json();
      if (!Array.isArray(data)) throw new Error("Received unexpected data format for permissions.");

      const permissions = data.sort((a, b) => a.title.localeCompare(b.title));
      setAllSystemPermissions(permissions);
    } catch (error) {
      setFetchSystemPermissionsError(`Error fetching system permissions: ${error.message}`);
      setAllSystemPermissions([]);
    } finally {
      setIsLoadingSystemPermissions(false);
    }
  }, []);

  useEffect(() => {
    checkUserRole();
    fetchRoles();
    fetchAllSystemPermissions();
  }, [checkUserRole, fetchRoles, fetchAllSystemPermissions]);

  useEffect(() => {
    if (selectedRole && allSystemPermissions.length > 0) {
      const activePermissionIds = new Set();

      if (Array.isArray(selectedRole.permissions)) {
        selectedRole.permissions.forEach(p => activePermissionIds.add(p.permissionId));
      }

      if (selectedRole.defaultPermissions && Array.isArray(selectedRole.defaultPermissions.defaultPermissions)) {
        selectedRole.defaultPermissions.defaultPermissions.forEach(p => activePermissionIds.add(p.permissionId));
      }
      
      const permissionsMap = {};
      allSystemPermissions.forEach(sysPerm => {
        permissionsMap[sysPerm.title] = activePermissionIds.has(sysPerm.permissionId);
      });
      setCurrentRolePermissions(permissionsMap);
    } else {
      setCurrentRolePermissions({});
    }
  }, [selectedRole, allSystemPermissions]);

  const canEditPermissions = isSuperAdmin || isAdminUser;

  const handlePermissionChange = (permissionTitle) => {
    if (!canEditPermissions) return;
    setCurrentRolePermissions(prev => ({ ...prev, [permissionTitle]: !prev[permissionTitle] }));
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleUpdateRolePermissions = async () => {
    if (!selectedRole) return;
    setIsSubmittingPermissions(true);
    setSuccessMessage('');
    setErrorMessage('');
    const permissionIdsToSave = allSystemPermissions.filter(p => currentRolePermissions[p.title]).map(p => p.permissionId);
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) { setErrorMessage("Authentication token not found."); setIsSubmittingPermissions(false); return; }
    try {
      const queryParams = new URLSearchParams({ roleId: selectedRole.roleId.toString() });
      const response = await fetch(`${API_BASE_URL}/api/update-permissions?${queryParams.toString()}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(permissionIdsToSave),
      });
      const responseData = await response.json().catch(() => ({ message: response.ok ? "Permissions updated." : "Operation failed." }));
      if (!response.ok) throw new Error(responseData?.message || `API Error: ${response.status}`);
      setSuccessMessage("Permissions updated successfully!");
      const updatedRoles = await fetchRoles();
      const newSelectedRole = updatedRoles.find(r => r.roleId === selectedRole.roleId);
      if (newSelectedRole) setSelectedRole(newSelectedRole);
    } catch (error) {
      setErrorMessage(`Failed to update permissions: ${error.message}`);
    } finally {
      setIsSubmittingPermissions(false);
    }
  };

  const handleApplyPreset = async () => {
    const availablePresets = Array.from(presetsWithDetails.values());
    if (!selectedRole || availablePresets.length === 0) return;

    const defaultPresetToApply = availablePresets[0]; // Apply the first available preset
    const presetIdToApply = defaultPresetToApply.id;

    const presetDetails = presetsWithDetails.get(parseInt(presetIdToApply));
    if (!presetDetails || !presetDetails.permissions) {
        setErrorMessage("Could not find permission details for the default preset.");
        return;
    }
    const presetPermissionIds = presetDetails.permissions.map(p => p.permissionId);

    setIsApplyingPreset(true);
    setSuccessMessage('');
    setErrorMessage('');
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      setErrorMessage("Authentication token not found.");
      setIsApplyingPreset(false);
      return;
    }
    
    try {
      const updateParams = new URLSearchParams({ roleId: selectedRole.roleId.toString() });
      const updateResponse = await fetch(`${API_BASE_URL}/api/update-permissions?${updateParams.toString()}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(presetPermissionIds),
      });

      if (!updateResponse.ok) {
          const errorData = await updateResponse.json().catch(() => null);
          throw new Error(errorData?.message || "Failed to clear old permissions before applying preset.");
      }

      const activateParams = new URLSearchParams({ roleId: selectedRole.roleId.toString(), defaultPermissionId: presetIdToApply.toString() });
      const activateResponse = await fetch(`${API_BASE_URL}/api/activate-default-permissions?${activateParams.toString()}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });

      if (!activateResponse.ok) {
          const errorData = await activateResponse.json().catch(() => null);
          throw new Error(errorData?.message || "Failed to activate preset after updating permissions.");
      }

      setSuccessMessage("Default Permission Set applied successfully! Role permissions have been replaced.");
      
      const updatedRoles = await fetchRoles();
      const newSelectedRole = updatedRoles.find(r => r.roleId === selectedRole.roleId);
      if (newSelectedRole) {
        setSelectedRole(newSelectedRole);
      }

    } catch (error) {
      setErrorMessage(`Failed to apply preset: ${error.message}`);
    } finally {
      setIsApplyingPreset(false);
    }
  };

  const openCreateRoleModal = () => {
    setIsCreateRoleModalOpen(true);
    setNewRoleName('');
    setModalSuccessMessage('');
    setModalErrorMessage('');
  };

  const closeCreateRoleModal = () => setIsCreateRoleModalOpen(false);

  const handleCreateRoleSubmit = async (event) => {
    event.preventDefault();
    if (!newRoleName.trim()) { setModalErrorMessage("Role name cannot be empty."); return; }
    setIsSubmittingCreateRole(true);
    setModalErrorMessage('');
    setModalSuccessMessage('');
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) { setModalErrorMessage("Authentication token not found."); setIsSubmittingCreateRole(false); return; }
    try {
      const response = await fetch(`${API_BASE_URL}/api/create-role`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: newRoleName.trim() }),
      });
      const responseData = await response.json().catch(() => { throw new Error(`Server returned non-JSON response.`); });
      if (!response.ok) throw new Error(responseData.message || `API Error: ${response.status}`);
      setModalSuccessMessage(responseData.message || `Role created successfully!`);
      await fetchRoles();
      setTimeout(closeCreateRoleModal, 1500);
    } catch (error) {
      setModalErrorMessage(`Failed to create role: ${error.message}`);
    } finally {
      setIsSubmittingCreateRole(false);
    }
  };
  
  const openCreateDefaultSetModal = () => {
    setPermissionsForNewSet({});
    setModalSetErrorMessage('');
    setModalSetSuccessMessage('');
    setModalPermissionSearch('');
    setIsCreateDefaultSetModalOpen(true);
  };
  
  const closeCreateDefaultSetModal = () => setIsCreateDefaultSetModalOpen(false);
  
  const handleNewSetPermissionChange = (permissionId) => {
    setPermissionsForNewSet(prev => ({ ...prev, [permissionId]: !prev[permissionId] }));
  };

  const handleCreateDefaultSetSubmit = async (event) => {
    event.preventDefault();
    const selectedIds = Object.keys(permissionsForNewSet).filter(id => permissionsForNewSet[id]);
    if (selectedIds.length === 0) { setModalSetErrorMessage("You must select at least one permission."); return; }
    setIsSubmittingNewSet(true);
    setModalSetErrorMessage('');
    setModalSetSuccessMessage('');
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) { setModalSetErrorMessage("Authentication token not found."); setIsSubmittingNewSet(false); return; }
    const queryParams = new URLSearchParams();
    selectedIds.forEach(id => queryParams.append('permissionIds', id));
    try {
      const response = await fetch(`${API_BASE_URL}/api/assign-default-permissions-to-role?${queryParams.toString()}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
      });
      const responseData = await response.json();
      if (!response.ok) throw new Error(responseData.message || `API Error: ${response.status}`);
      
      const { defaultPermissionId, defaultPermissions } = responseData.default_permissions;
      const newSet = {
          id: defaultPermissionId,
          name: `Preset #${defaultPermissionId}`,
          permissions: defaultPermissions || []
      };
      
      setPresetsWithDetails(prev => new Map(prev).set(newSet.id, newSet));
      setModalSetSuccessMessage(`Preset #${defaultPermissionId} created successfully!`);
      setTimeout(closeCreateDefaultSetModal, 2000);
    } catch (error) {
      setModalSetErrorMessage(`Failed to create preset: ${error.message}`);
    } finally {
      setIsSubmittingNewSet(false);
    }
  };

  const availablePresets = Array.from(presetsWithDetails.values());
  const filteredPermissions = allSystemPermissions.filter(p => p.title.replace(/_/g, ' ').toLowerCase().includes(permissionSearchQuery.toLowerCase()));
  const filteredModalPermissions = allSystemPermissions.filter(p => p.title.replace(/_/g, ' ').toLowerCase().includes(modalPermissionSearch.toLowerCase()));

  return (
    <div className="role-permission-management-section">
      <style>{styles}</style>
      <h2 className="rpm-title">Role & Permission Management</h2>
      <p className="rpm-subtitle">Manage user roles and their permissions</p>

      <div className="rpm-tabs">
        {isSuperAdmin && <button className={`rpm-tab ${activeTab === 'manage' ? 'active' : ''}`} onClick={() => setActiveTab('manage')}>👥 Manage Roles</button>}
        {(isSuperAdmin || isAdminUser || isReportingManager) && <button className={`rpm-tab ${activeTab === 'assign' ? 'active' : ''}`} onClick={() => setActiveTab('assign')}>➕ Assign Roles</button>}
      </div>

      {activeTab === 'manage' && isSuperAdmin && (
        <>
          <div className="roles-and-permissions-header">
            <h3 className="roles-and-permissions-section-title">Roles and Permissions</h3>
            <div className="action-buttons-group">
              <button className="button-primary" onClick={openCreateRoleModal} disabled={isSubmittingCreateRole}>{isSubmittingCreateRole ? 'Processing...' : 'Create Role'}</button>
              <button className="button-secondary" onClick={openCreateDefaultSetModal}>Create Default Set</button>
            </div>
          </div>
          <div className="roles-layout">
            <div className="available-roles-panel">
              <h4>Available Roles</h4>
              {isLoadingRoles && <p style={{ padding: '20px' }}>Loading roles...</p>}
              {!isLoadingRoles && errorMessage && (
                <div className="form-message error" style={{ margin: '10px 20px' }}>
                  {errorMessage}
                  <button onClick={fetchRoles} disabled={isLoadingRoles} style={{ marginLeft: '10px' }}>Retry</button>
                </div>
              )}
              {!isLoadingRoles && roles.length > 0 ? (
                <ul className="roles-list">
                  {roles.map(role => (
                    <li key={role.roleId} className={`role-list-item ${selectedRole?.roleId === role.roleId ? 'selected' : ''}`}
                      onClick={() => { setSelectedRole(role); setSuccessMessage(''); setErrorMessage(''); }}>
                      <span className="role-name">{role.name}</span>
                    </li>
                  ))}
                </ul>
              ) : (!isLoadingRoles && !errorMessage && <p style={{ padding: '20px' }}>No roles found.</p>)}
            </div>
            <div className="role-permissions-panel">
              <div className="role-permissions-panel-header permissions-header-actions">
                <h4>Permissions for: <strong>{selectedRole ? selectedRole.name : "N/A"}</strong></h4>
                {selectedRole && canEditPermissions && (
                  <div className="action-buttons-group">
                    <button className="button-secondary" onClick={handleUpdateRolePermissions} disabled={isSubmittingPermissions || isLoadingSystemPermissions}>
                      {isSubmittingPermissions ? 'Updating...' : 'Update Permissions'}
                    </button>
                    <div className="preset-apply-group">
                      <button className="button-secondary" onClick={handleApplyPreset} disabled={isApplyingPreset || availablePresets.length === 0}>
                        {isApplyingPreset ? 'Applying...' : 'Apply Default Permission Set'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {selectedRole ? (
                <div className="permissions-grid-container">
                  {successMessage && <div className="form-message success" style={{ marginBottom: '15px' }}>{successMessage}</div>}
                  {errorMessage && !successMessage && <div className="form-message error" style={{ marginBottom: '15px' }}>{errorMessage}</div>}
                  {isLoadingSystemPermissions && <p>Loading system permissions...</p>}
                  {fetchSystemPermissionsError && <div className="form-message error" style={{ marginBottom: '15px' }}>{fetchSystemPermissionsError}<button onClick={fetchAllSystemPermissions} style={{ marginLeft: '10px' }}>Retry</button></div>}
                  {!isLoadingSystemPermissions && !fetchSystemPermissionsError && allSystemPermissions.length > 0 && <input type="text" className="permission-search-input" placeholder="Search permissions..." value={permissionSearchQuery} onChange={(e) => setPermissionSearchQuery(e.target.value)} />}
                  {!isLoadingSystemPermissions && !fetchSystemPermissionsError && filteredPermissions.length > 0 ? (
                    <div className="permissions-grid">
                      {filteredPermissions.map(permission => (
                        <div key={permission.permissionId} className="permission-item">
                          <label><input type="checkbox" checked={currentRolePermissions[permission.title] || false} onChange={() => handlePermissionChange(permission.title)} disabled={!canEditPermissions || isSubmittingPermissions} />{permission.title.replace(/_/g, ' ')}</label>
                        </div>
                      ))}
                    </div>
                  ) : (!isLoadingSystemPermissions && !fetchSystemPermissionsError && <p style={{ marginTop: '20px', textAlign: 'center' }}>{allSystemPermissions.length === 0 ? 'No system permissions defined.' : 'No permissions found for your search.'}</p>)}
                </div>
              ) : (
                <div className="no-role-selected-placeholder">
                  <div className="placeholder-icon-container"><span className="placeholder-question-icon">?</span></div>
                  <p className="placeholder-title">No Role Selected</p>
                  <span className="placeholder-subtitle">Select a role from the list to view and manage its permissions.</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'assign' && (isSuperAdmin || isAdminUser || isReportingManager) &&
        <AssignRolesContent allRoles={roles} isSuperAdmin={isSuperAdmin} isAdminUser={isAdminUser} isReportingManager={isReportingManager} />}
      
      {isCreateRoleModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Create New Role</h2>
            {modalSuccessMessage && <div className="form-message success">{modalSuccessMessage}</div>}
            {modalErrorMessage && <div className="form-message error">{modalErrorMessage}</div>}
            <form onSubmit={handleCreateRoleSubmit} style={{ opacity: modalSuccessMessage ? 0.5 : 1 }}>
              <div className="form-group">
                <label htmlFor="roleNameModal">Role Name <span className="required-indicator">*</span></label>
                <input type="text" id="roleNameModal" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} placeholder="Enter role name (e.g., Developer)" required autoFocus disabled={isSubmittingCreateRole || !!modalSuccessMessage}/>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={closeCreateRoleModal} className="button-cancel" disabled={isSubmittingCreateRole}>Cancel</button>
                <button type="submit" className="button-create" disabled={isSubmittingCreateRole || !newRoleName.trim() || !!modalSuccessMessage}>{isSubmittingCreateRole ? 'Creating...' : 'Create Role'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      
       {isCreateDefaultSetModalOpen && (
        <div className="modal-overlay">
            <div className="modal-content preset-modal">
                <h2>Create Default Permission Set</h2>
                {modalSetSuccessMessage && <div className="form-message success">{modalSetSuccessMessage}</div>}
                {modalSetErrorMessage && <div className="form-message error">{modalSetErrorMessage}</div>}
                <form onSubmit={handleCreateDefaultSetSubmit} style={{ opacity: modalSetSuccessMessage ? 0.5 : 1 }}>
                    <div className="form-group">
                        <label>Select Permissions</label>
                         <input type="text" placeholder="Search permissions..." value={modalPermissionSearch} onChange={(e) => setModalPermissionSearch(e.target.value)} style={{marginBottom: '16px'}}/>
                        <div className="modal-permissions-list">
                          {filteredModalPermissions.length > 0 ? filteredModalPermissions.map(permission => (
                            <div key={permission.permissionId} className="modal-permission-item">
                              <label>
                                <input type="checkbox" checked={permissionsForNewSet[permission.permissionId] || false} onChange={() => handleNewSetPermissionChange(permission.permissionId)} disabled={isSubmittingNewSet}/>
                                {permission.title.replace(/_/g, ' ')}
                              </label>
                            </div>
                          )) : <p style={{textAlign: 'center', padding: '10px'}}>No permissions found.</p>}
                        </div>
                    </div>
                    <div className="modal-actions">
                        <button type="button" onClick={closeCreateDefaultSetModal} className="button-cancel" disabled={isSubmittingNewSet}>Cancel</button>
                        <button type="submit" className="button-create" disabled={isSubmittingNewSet || !!modalSetSuccessMessage}>{isSubmittingNewSet ? 'Creating...' : 'Create Preset'}</button>
                    </div>
                </form>
            </div>
        </div>
       )}
    </div>
  );
};

export default PermissionsPage;